import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMetric, metricReasonKey, readMetric, reactionField } from '../../lib/socialMetrics.js';
import { Icons as I } from '../pack-icons';
import '../../styles/social-metrics.css';

const metricIcons = {
  views: I.eye,
  reactionsTotal: I.heart,
  likes: I.heart,
  comments: I.chat,
  shares: I.share,
  saves: I.bookmark,
  impressions: I.trend,
  reach: I.users,
};

export function MetricValue({ metrics, field, detail = false, compact = false, label }) {
  const { t, i18n } = useTranslation();
  const reading = readMetric(metrics, field);
  const name = label || t(`socialMetrics.${field}`);
  const value = formatMetric(reading.value, i18n.language);
  const notes = [];
  if (reading.value === null) notes.push(t(`socialMetrics.${metricReasonKey(reading.reason)}`));
  if (reading.stale) notes.push(t('socialMetrics.stale'));
  if (reading.partial && reading.value !== null) notes.push(t('socialMetrics.partial'));
  if (reading.bestEffort) notes.push(t('socialMetrics.bestEffort'));
  if (reading.officialTelegram) notes.push(t('socialMetrics.telegramOfficial'));
  if (reading.stale && reading.reason) notes.push(t(`socialMetrics.${metricReasonKey(reading.reason)}`));
  const provenance = [];
  if (reading.value !== null && reading.sourceLabelKey) provenance.push(t('socialMetrics.sourceDetail', { source: t(`socialMetrics.${reading.sourceLabelKey}`) }));
  if (reading.value !== null && reading.providerMetricLabelKey) provenance.push(t('socialMetrics.providerMetricDetail', { metric: t(`socialMetrics.${reading.providerMetricLabelKey}`) }));
  const date = reading.value !== null && Number.isFinite(Date.parse(reading.measuredAt))
    ? t('socialMetrics.asOf', { date: new Date(reading.measuredAt).toLocaleString(i18n.language) }) : null;
  const description = [name, value, ...notes, ...provenance, date].filter(Boolean).join(' · ');
  const compactNotes = reading.value === null ? notes.slice(0, 1)
    : notes.filter(note => !reading.reason || note !== t(`socialMetrics.${metricReasonKey(reading.reason)}`));
  const showCompactIndicator = compact && reading.value !== null
    && (reading.stale || reading.partial || reading.bestEffort);
  return (
    <span className={`metric-reading${compact ? ' is-compact' : ''}`} role="group" data-metric={field} data-state={reading.value === null ? 'unavailable' : reading.stale ? 'stale' : 'measured'} title={description} aria-label={description} tabIndex={compact && notes.length > 0 ? 0 : undefined}>
      <span className="mono metric-number" aria-hidden="true">{value}</span>
      {showCompactIndicator && <span className="metric-compact-indicator" aria-hidden="true" />}
      {!compact && notes.length > 0 && <span className="metric-note" aria-hidden="true">{(detail ? notes : compactNotes).join(' · ')}</span>}
      {!compact && date && (detail || reading.stale) && <span className="metric-note" aria-hidden="true">{detail ? date : t('socialMetrics.asOf', { date: new Date(reading.measuredAt).toLocaleDateString(i18n.language) })}</span>}
    </span>
  );
}

export function MetricGrid({ metrics, platform, fields, compact = false }) {
  const { t } = useTranslation();
  const platformFields = String(platform || '').toLowerCase() === 'telegram'
    ? ['views', reactionField(metrics, platform), 'comments']
    : ['views', reactionField(metrics, platform), 'comments', 'shares', 'saves'];
  const displayed = fields || [...platformFields,
    ...['impressions', 'reach'].filter(field => readMetric(metrics, field).value !== null)];
  return <div className="metric-grid">
    {displayed.map(field => {
      const Icon = metricIcons[field] || I.trend;
      return <div className={`metric-cell metric-cell--${field}`} key={field}>
        <div className="metric-cell-heading">
          <span className="metric-cell-icon" aria-hidden="true"><Icon size={16} /></span>
          <span className="metric-label">{t(`socialMetrics.${field}`)}</span>
        </div>
        <MetricValue metrics={metrics} field={field} detail compact={compact} />
      </div>;
    })}
  </div>;
}

export function MetricSeriesTable({ data, fields, label }) {
  const { t, i18n } = useTranslation();
  return <details className="metric-series-table">
    <summary>{t('socialMetrics.chartData')}</summary>
    <div className="metric-table-scroll"><table>
      <caption>{label} · {t('socialMetrics.chartHint')}</caption>
      <thead><tr><th scope="col">{t('socialMetrics.date')}</th>{fields.map(field => <th scope="col" key={field.key}>{field.label}</th>)}</tr></thead>
      <tbody>{data.map((row, index) => <tr key={row.date || index}>
        <th scope="row">{row.label || row.date}</th>
        {fields.map(field => <td key={field.key}>{row[field.key] == null ? t('socialMetrics.unavailable') : formatMetric(row[field.key], i18n.language)}</td>)}
      </tr>)}</tbody>
    </table></div>
  </details>;
}
